import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Loader2, ZoomIn, ZoomOut, Move } from 'lucide-react';

const FRAME = 288; // on-screen square crop frame (px)
const OUTPUT = 640; // saved crop size (px)

// Square crop/reposition dialog: drag to pan, slider/wheel to zoom.
// Returns a 640x640 JPEG blob via onCropped.
export const PhotoCropDialog = ({ open, src, busy, onCancel, onCropped }) => {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    if (!open || !src) { setImg(null); return; }
    const el = new Image();
    el.onload = () => {
      setImg(el);
      setZoom(1);
      const base = FRAME / Math.min(el.naturalWidth, el.naturalHeight);
      setOffset({ x: (FRAME - el.naturalWidth * base) / 2, y: (FRAME - el.naturalHeight * base) / 2 });
    };
    el.src = src;
  }, [open, src]);

  const base = img ? FRAME / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const scale = base * zoom;

  const clamp = useCallback((o, s) => ({
    x: Math.min(0, Math.max(FRAME - (img?.naturalWidth || 0) * s, o.x)),
    y: Math.min(0, Math.max(FRAME - (img?.naturalHeight || 0) * s, o.y)),
  }), [img]);

  const setZoomKeepCenter = (z) => {
    const nz = Math.min(5, Math.max(1, z));
    const s = base * nz;
    const c = FRAME / 2;
    setOffset((o) => clamp({ x: c - (c - o.x) * (s / scale), y: c - (c - o.y) * (s / scale) }, s));
    setZoom(nz);
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setOffset(clamp({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) }, scale));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const handleSave = () => {
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, -offset.x / scale, -offset.y / scale, FRAME / scale, FRAME / scale, 0, 0, OUTPUT, OUTPUT);
    canvas.toBlob((blob) => { if (blob) onCropped(blob); }, 'image/jpeg', 0.88);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle>Position Vehicle Photo</DialogTitle>
          <DialogDescription>Drag to move and zoom to choose the square section that will be shown.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <div
            className="relative rounded-lg overflow-hidden border bg-muted touch-none cursor-grab active:cursor-grabbing select-none"
            style={{ width: FRAME, height: FRAME }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={(e) => setZoomKeepCenter(zoom * (e.deltaY < 0 ? 1.1 : 0.9))}
            data-testid="photo-crop-canvas"
          >
            {img ? (
              <img
                src={src}
                alt="Crop preview"
                draggable={false}
                className="absolute top-0 left-0 max-w-none origin-top-left pointer-events-none"
                style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, width: img.naturalWidth, height: img.naturalHeight }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
            )}
            <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/30" />
          </div>
          <div className="flex items-center gap-3 w-full px-1">
            <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
            <Slider value={[zoom]} min={1} max={5} step={0.05} onValueChange={([v]) => setZoomKeepCenter(v)} className="flex-1" data-testid="photo-crop-zoom" />
            <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Move className="w-3 h-3" /> Drag the image to reposition · saved as a {OUTPUT}×{OUTPUT} square</p>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onCancel} data-testid="photo-crop-cancel">Cancel</Button>
          <Button disabled={busy || !img} onClick={handleSave} data-testid="photo-crop-save">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : 'Save Photo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
